import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { AppEvent } from '../types/models/notifications/app-event';
import { TaskActivity } from '../types/models/task-activities/task-activity';
import { DataService } from './data-service';
import { TaskActivityDTO } from './dtos/task-activities/task-activity.dto';
import { TaskActivityFactory } from './factories/task-activities/task-activity.factory';

@Injectable({ providedIn: 'root' })
export class TaskActivitiesDataService extends DataService<TaskActivityDTO, TaskActivity> {
  private readonly activities: DataStore<TaskActivity> = new DataStore<TaskActivity>();
  private readonly taskActivities: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  private readonly http: HttpClient = inject(HttpClient);
  private readonly taskActivityFactory: TaskActivityFactory = inject(TaskActivityFactory);

  public getById(id: string): Signal<TaskActivity> {
    return this.activities.get(id);
  }

  protected handleEvent(event: AppEvent): void {
    if (event.eventType === 'TaskDataChanged' || event.eventType === 'TaskGroupChanged') {
      // task Id
      const data: string = event.data as string;
      this.fetchTaskActivities(data);
    }
  }

  public getTaskActivities(taskId: string): Signal<TaskActivity[]> {
    if (!this.taskActivities.hasDataForId(taskId)) {
      this.fetchTaskActivities(taskId);
    }

    return computed(() => {
      const activityIds = this.taskActivities.get(taskId)()?.ids || [];
      return activityIds.map(id => this.activities.get(id)());
    });
  }

  public reloadTaskActivities(taskId: string): void {
    this.fetchTaskActivities(taskId);
  }

  private fetchTaskActivities(taskId: string): void {
    this.http.get<TaskActivityDTO[]>(`${environment.APIEndPoint}tasks/${taskId}/activities`).subscribe(activities => {
      if (activities.length) {
        this.activities.upsertMany(activities.map(dto => this.taskActivityFactory.produce(dto)));
      }

      this.taskActivities.upsert({
        id: taskId,
        ids: activities.length ? activities.map(a => a.id) : []
      });
    });
  }

  public upsertLocalData(dto: TaskActivityDTO): Signal<TaskActivity> {
    const activity = this.taskActivityFactory.produce(dto);
    return activity ? this.activities.upsert(activity) : signal(undefined);
  }
}
